import React from 'react';
import { useTranslation } from 'react-i18next';

import PeriodInputs, { ITimeChange } from 'components/Controls/PeriodInputs';
import { INPUT_MODES } from 'components/Controls/PeriodInputs/helpers';
import SwitchInput from 'components/Controls/SwitchInput';
import {
  FILTER_OPERATIONS,
  IFilterValue,
  ISelectFilterProps,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { MAX_MSEC } from 'constants/common';
import { INullable } from 'utils/types';

interface IFilterPeriodInput {
  columnKey: string;
  focusedFieldName: INullable<{ columnKey: string; index?: number }>;
  index?: number;
  inputsMode?: INPUT_MODES;
  isAutoFocused: boolean;
  isUnlimitedHoursFilter?: boolean;
  multiSelect?: boolean;
  onBlur: () => void;
  onSelectFilter: (data: ISelectFilterProps) => void;
  operation: INullable<FILTER_OPERATIONS>;
  value?: IFilterValue;
}

const FilterPeriodInput = ({
  columnKey,
  focusedFieldName,
  index = 0,
  inputsMode,
  isAutoFocused,
  isUnlimitedHoursFilter,
  multiSelect = false,
  onBlur,
  onSelectFilter,
  operation,
  value,
}: IFilterPeriodInput) => {
  const { t } = useTranslation();

  const unlimitedHandler = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onSelectFilter({ columnKey, operation, value: checked ? MAX_MSEC : undefined });
    },
    [onSelectFilter, operation, columnKey],
  );

  const onPeriodChange: ITimeChange = ({ msecs }) => {
    let newValue: string | string[] | undefined = msecs ? msecs.toString() : undefined;
    if (msecs) {
      if (multiSelect) {
        newValue = Array.isArray(value)
          ? (value as string[]).map((item, itemIndex) =>
              index === itemIndex ? msecs.toString() : item,
            )
          : [msecs.toString()];
      }
    }
    onSelectFilter({ columnKey, operation, value: newValue });
  };

  return (
    <div>
      {isUnlimitedHoursFilter && (
        <SwitchInput
          checked={value === MAX_MSEC}
          label={t('common.filters.unlimited')}
          onChange={unlimitedHandler}
        />
      )}
      {value !== MAX_MSEC && (
        <PeriodInputs
          hideLabel={!isUnlimitedHoursFilter}
          inputsMode={inputsMode}
          isAutoFocused={
            multiSelect
              ? isAutoFocused && (focusedFieldName?.index || 0) === index
              : isAutoFocused
          }
          onBlur={onBlur}
          onTimeChange={onPeriodChange}
          sizeVariant='sm'
          value={
            (multiSelect && Array.isArray(value)
              ? Number((value as string[])?.[index])
              : Number(value as string)) || 0
          }
        />
      )}
    </div>
  );
};

export default FilterPeriodInput;
